import { useLazyQuery } from "@apollo/client/react";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CustomButton } from '../../../../../../../components/customButton/CustomButton';
import { CustomDialog } from '../../../../../../../components/customDialog/CustomDialog';
import { generateColumnsDefaultConfiguration } from '../../../../../../../components/dataGrid/columnHelpers';
import DataGridGenerico from '../../../../../../../components/dataGrid/DataGridGenerico';
import { RenderInputByTypeMobile } from '../../../../../../../components/formGroupGenerator/RenderInputByTypeMobile';
import { adaptInsumoByEvaluacionArray, GET_INSUMOS_VISITA_BY_EVALUACION } from '../../../../../../../data/graphQL/queries/MisVisitas/getInsumosIndicacion';
import { useDebounce } from '../../../../../../../hooks/useDebounce';

export const IndicacionesVisitaInput = ({
  idEvaluacion,
  readOnly,
  handleChange,
  indicaciones = [],
  inputName,
  solicitudIndicacionPendiente
}) => {
  const columns = generateColumnsDefaultConfiguration([
  ...(!readOnly ? [{
    field: 'action',
    headerName: '',
    flex: 3,
    renderCell: (cellProps) => {
      const { row } = cellProps;
      return (
        !solicitudIndicacionPendiente && <DeleteOutlineIcon className='edit-icon' onClick={() => deleteIndicacion(row)} />
      );
    }
  }] : []),
  {
    headerName: 'Producto',
    field: 'nombreInsumo',
    flex: 57
  },
  {
    headerName: 'Cant. Diaria',
    field: 'cantidadDiaria',
    flex: 25
  },
  {
    headerName: 'Unidad',
    field: 'unidad',
    flex: 20
  }]
  );

  const [openformModal, setOpenformModal] = useState(false);
  const [productoSelected, setProductoSelected] = useState({});
  const [cantidadDiaria, setCantidadDiaria] = useState();

  const [
  insumos,
  filterStatus,
  insumosLoading,
  setFilterStatus] =
  useGetInsumos(idEvaluacion);

  const deleteIndicacion = (indicacion) => {
    const newIndicaciones = indicaciones.filter((i) => i.idInsumo !== indicacion.idInsumo);

    handleChange(inputName, newIndicaciones);
  };

  const handleOpenFormModal = () => {
    if (!solicitudIndicacionPendiente)
      setOpenformModal(true);
    else
      toast.error("Existe un cambio de indicación en curso para el contrato.");
  }

  const handleCloseFormModal = () => {
    setOpenformModal(false);
    setProductoSelected({});
    setCantidadDiaria();
  };

  const handleSelectInsumo = (value) => {
    setProductoSelected(value);
  };

  const onSubmit = () => {

    if (indicaciones.find((i) => i.idInsumo === productoSelected.id)) {
      toast.error('El producto ya fue agregado');
      return;
    }

    const newIndicacion = {
      idInsumo: productoSelected.id,
      nombreInsumo: productoSelected.label,
      unidad: productoSelected.unidad,
      cantidadDiaria: parseFloat(cantidadDiaria),
      proteinas: productoSelected.proteinas,
      calorias: productoSelected.calorias
    };
    const newIndicaciones = [...indicaciones, newIndicacion];
    handleChange(inputName, newIndicaciones);
    handleCloseFormModal();

  };

  return (
    <>
            <Box sx={{ width: '100%' }}>
                {!readOnly && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <CustomButton
                    onClick={handleOpenFormModal}
                    size='small'
                    variant='outlined'>            
                      Agregar
                  </CustomButton>
                </Box>
        }
          <DataGridGenerico
          columns={columns}
          rows={indicaciones}
          toolbar={false}
          hideFooter />
        
            </Box>
            <CustomDialog
        maxWidth='md'
        fullWidth
        onCancel={handleCloseFormModal}
        open={openformModal}
        cancelText='Cancelar'
        onAccept={onSubmit}
        acceptDisabled={!productoSelected?.id || !cantidadDiaria}
        acceptText='Agregar'
        title='Indicaciones'>
        
        <Autocomplete
          options={Array.isArray(insumos) ? insumos : []}
          disableClearable
          noOptionsText={filterStatus ? 'No hay coincidencias' : 'Escriba para buscar'}
          onChange={(_, newValue) => handleSelectInsumo(newValue)}
          onInputChange={(_, value) => setFilterStatus(value)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderOption={(props, option) => {
            return (
              <Box {...props} key={option.id}>
                  {option?.label}
              </Box>);
          }}
          renderInput={(params) =>
          <TextField
            {...params}
            sx={{ '.MuiOutlinedInput-root': { paddingRight: '15px !important' } }}
            label='Producto'
            margin='none'
            size='small'
            InputProps={{
              ...params.InputProps,
              endAdornment:
              <InputAdornment position='end'>
                                        {insumosLoading ?
                <CircularProgress color='inherit' size={20} /> :
                <SearchIcon className='search-icon' />}
                                    </InputAdornment>

            }} />
          } />
        
                <RenderInputByTypeMobile
          grided
          handleChange={(_, value) => setCantidadDiaria(value)}
          input={{
            name: 'cantidadDiaria',
            displayName: 'Cant. diaria',
            type: 'float',
            required: true
          }}
          value={cantidadDiaria} />
        
                <RenderInputByTypeMobile
          grided
          input={{
            name: 'unidad',
            displayName: 'Unidad',
            type: 'string',
            required: true,
            disabled: true
          }}
          value={productoSelected?.unidad} />
        
            </CustomDialog>
        </>);

};

const useGetInsumos = (idEvaluacion) => {
  const [insumos, setInsumos] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const debouncedFilterValue = useDebounce(filterStatus, 800);

  const [getInsumos, { data, loading }] =
    useLazyQuery(GET_INSUMOS_VISITA_BY_EVALUACION, {
      fetchPolicy: 'network-only'
    });

  // 🔹 Carga inicial (trae todo)
  useEffect(() => {
    if (!idEvaluacion) return;

    getInsumos({
      variables: {
        idEvaluacion,
        where: null
      }
    });
  }, [idEvaluacion]);

  // 🔹 Filtrado
  useEffect(() => {
    if (!idEvaluacion) return;
    if (!debouncedFilterValue.trim()) return;

    getInsumos({
      variables: {
        idEvaluacion,
        where: {
          descripcion: { contains: debouncedFilterValue }
        }
      }
    });
  }, [debouncedFilterValue, idEvaluacion]);

  useEffect(() => {
    if (!data) return;
    const list = data.allInsumosVisitaByIdEvaluacion;
    setInsumos(Array.isArray(list) ? list : []);
  }, [data]);

  return [
    adaptInsumoByEvaluacionArray(insumos),
    filterStatus,
    loading,
    setFilterStatus
  ];
};
